import { Node, bin, dec, frac, int, of, pct, power, root } from "./expression.ts";

/*
 * Every way to write a given value, by kind of expression (ADR 0127 §2, §9). A
 * function here takes a target and lists the expressions that equal it; it does
 * not choose, and it does not check (equalGroups.ts keeps only those whose
 * exact value is the target and that are not trivial). Order is fixed by the
 * loops, since a board's choice among them is a position in this list.
 *
 * Grades 2 to 4 write a group with one tile per operation. Grades 5 to 8 write it
 * with one tile per family of expression. grades.ts says which grade uses which.
 */

export type Builder = (value: number) => Node[];

const range = (from: number, to: number): number[] => {
    const out: number[] = [];
    for (let i = from; i <= to; i++) {
        out.push(i);
    }
    return out;
};

// ── Whole numbers, grades 2 to 5 ─────────────────────────────────────────────

/** The four operations a grade 2 to 4 group mixes (ADR 0127 §2). */
export type OpKind = "+" | "−" | "×" | "÷";

/** a + b with both addends at least `least`. */
const sums =
    (least: number): Builder =>
    (v) =>
        range(least, v - least).map((a) => bin("+", int(a), int(v - a)));

/**
 * a − b with the subtrahend `least` to `most` and the minuend no more than `ceiling`:
 * a take-away, not a distance.
 */
const differences =
    (least: number, most: number, ceiling: number): Builder =>
    (v) =>
        range(least, Math.min(most, ceiling - v)).map((b) => bin("−", int(v + b), int(b)));

/** Grade 3: both factors 2 to 10, a times fact. */
const timesFacts: Builder = (v) =>
    range(2, 10)
        .filter((a) => v % a === 0 && v / a >= 2 && v / a <= 10)
        .map((a) => bin("×", int(a), int(v / a)));

/**
 * The one-digit factors that make `v` with a two-digit partner. A 2 is used only where nothing
 * bigger does, or every board would be a column of "2 × 32", "2 × 35".
 */
const digitFactors = (v: number): number[] => {
    const all = range(2, 9).filter((a) => v % a === 0 && v / a >= 10 && v / a <= 99);
    const bigger = all.filter((a) => a >= 3);
    return bigger.length > 0 ? bigger : all;
};

/** Grade 4: one digit times two digits, the digit first: "4 × 18". */
const digitTimesTwoDigit: Builder = (v) => digitFactors(v).map((a) => bin("×", int(a), int(v / a)));

/** Grade 5: two digits times one digit, the two digits first: "12 × 5". */
const twoDigitTimesDigit: Builder = (v) => digitFactors(v).map((a) => bin("×", int(v / a), int(a)));

/** Exact division by `least` to `most`, the dividend no more than `ceiling`. */
const quotients =
    (least: number, most: number, ceiling: number): Builder =>
    (v) =>
        range(least, most)
            .filter((b) => v * b <= ceiling)
            .map((b) => bin("÷", int(v * b), int(b)));

/** Grade 2: addition and subtraction within 20, nothing bigger than 20 on a tile. */
export const G2_KINDS: Record<"+" | "−", Builder> = {
    "+": sums(2),
    "−": differences(2, 18, 20),
};

/** Grade 3: sums and differences within 100 (a take-away of 2 to 25 from at most 60), times facts, exact division. */
export const G3_KINDS: Record<OpKind, Builder> = {
    "+": sums(2),
    "−": differences(2, 25, 60),
    "×": timesFacts,
    "÷": quotients(2, 10, 100),
};

/** Grade 4: within 200, one digit times two digits, exact division by one digit. */
export const G4_KINDS: Record<OpKind, Builder> = {
    "+": sums(5),
    "−": differences(5, 80, 200),
    "×": digitTimesTwoDigit,
    "÷": quotients(2, 9, 999),
};

/** Grade 5, family s: two-digit sums and differences. */
const bigSumsAndDifferences: Builder = (v) => [...sums(10)(v), ...differences(10, 99, 200)(v)];

/** Grade 5, family p: "3 × (4 + 2)", "(9 − 3) × 4", "(12 + 8) ÷ 4". */
const parentheses: Builder = (v) => {
    const out: Node[] = [];
    for (const a of range(2, 9)) {
        if (v % a === 0) {
            const inside = v / a;
            for (const b of range(2, inside - 2)) {
                out.push(bin("×", int(a), bin("+", int(b), int(inside - b))));
            }
            for (const c of range(2, 12)) {
                out.push(bin("×", int(a), bin("−", int(inside + c), int(c))));
                out.push(bin("×", bin("−", int(inside + c), int(c)), int(a)));
            }
            for (const b of range(2, inside - 2)) {
                out.push(bin("×", bin("+", int(b), int(inside - b)), int(a)));
            }
        }
        const total = v * a;
        if (total <= 150) {
            for (const b of range(2, Math.floor(total / 2))) {
                out.push(bin("÷", bin("+", int(b), int(total - b)), int(a)));
            }
        }
    }
    return out;
};

// ── Integers, fractions, decimals, percents, powers and roots, grades 5 to 8 ──

/** Family a: adding and subtracting integers, at least one negative number involved. */
function integerSums(v: number): Node[] {
    const out: Node[] = [];
    for (let a = 2; a <= 40; a++) {
        const y = v + a;
        if (y >= 2 && y <= 60) {
            out.push(bin("+", int(-a), int(y)), bin("+", int(y), int(-a)));
        }
        const x = v - a;
        if (Math.abs(x) >= 2 && x <= 60 && x >= -40) {
            out.push(bin("−", int(x), int(-a)));
        }
        const b = -v - a;
        if (b >= 2 && b <= 60) {
            out.push(bin("−", int(-a), int(b)));
        }
        const c = v + a;
        if (c >= 2 && c <= 40) {
            out.push(bin("−", int(-a), int(-c)));
        }
    }
    if (v < 0) {
        for (let x = 2; x <= 60; x++) {
            const y = x - v;
            if (y <= 60) {
                out.push(bin("−", int(x), int(y)));
            }
        }
    }
    return out;
}

/** Family b: multiplying and dividing integers with a negative among them. */
function integerProducts(v: number): Node[] {
    const out: Node[] = [];
    for (let p = 2; p <= 12; p++) {
        for (let q = 2; q <= 12; q++) {
            if (Math.abs(v) !== p * q) {
                continue;
            }
            if (v > 0) {
                out.push(bin("×", int(-p), int(-q)));
            } else {
                out.push(bin("×", int(-p), int(q)), bin("×", int(p), int(-q)));
            }
        }
    }
    for (let q = 2; q <= 12; q++) {
        const dividend = Math.abs(v) * q;
        if (dividend > 120) {
            continue;
        }
        if (v > 0) {
            out.push(bin("÷", int(-dividend), int(-q)));
        } else {
            out.push(bin("÷", int(dividend), int(-q)), bin("÷", int(-dividend), int(q)));
        }
    }
    return out;
}

const PROPER_FRACTIONS: [number, number][] = [
    [1, 2],
    [1, 3],
    [2, 3],
    [1, 4],
    [3, 4],
    [1, 5],
    [2, 5],
    [3, 5],
    [4, 5],
    [1, 8],
    [3, 8],
    [5, 8],
    [7, 8],
];

/** Family c: a fraction of a whole number: "¾ of 16". */
function fractionsOf(v: number): Node[] {
    if (v <= 0) {
        return [];
    }
    const out: Node[] = [];
    for (const [n, d] of PROPER_FRACTIONS) {
        const whole = (v * d) / n;
        if (Number.isInteger(whole) && whole >= 2 && whole <= 120) {
            out.push(of(frac(n, d), int(whole)));
        }
    }
    return out;
}

/** [units, places]: 0.5 is [5, 1], 0.25 is [25, 2]. */
const DECIMALS: [number, number][] = [
    [1, 1],
    [2, 1],
    [25, 2],
    [4, 1],
    [5, 1],
    [6, 1],
    [75, 2],
    [8, 1],
    [15, 1],
    [25, 1],
];

/** Family d: a decimal times a whole number: "0.5 × 24", "1.5 × 8". */
function decimalProducts(v: number): Node[] {
    if (v <= 0) {
        return [];
    }
    const out: Node[] = [];
    for (const [units, places] of DECIMALS) {
        const whole = (v * 10 ** places) / units;
        if (Number.isInteger(whole) && whole >= 2 && whole <= 120) {
            out.push(bin("×", dec(units, places), int(whole)), bin("×", int(whole), dec(units, places)));
        }
    }
    return out;
}

type Exponent = 2 | 3 | 4 | 5;

const SQUARES_AND_CUBES: [number, Exponent][] = [
    ...range(2, 10).map((b): [number, Exponent] => [b, 2]),
    ...range(2, 5).map((b): [number, Exponent] => [b, 3]),
];

/** Grade 8 adds the fourth and fifth powers that stay small. */
const HIGHER_POWERS: [number, Exponent][] = [
    [2, 4],
    [3, 4],
    [2, 5],
];

/**
 * Family e: a power with one operation: "2³ + 4", "5² − 13", "3² × 2". With
 * `negativeCubes`, a negative number cubed stays negative: "(−2)³ + 4".
 */
const powersOf =
    (bases: [number, Exponent][], negativeCubes: boolean): Builder =>
    (v) => {
        const out: Node[] = [];
        for (const [b, e] of bases) {
            const p = b ** e;
            const base = power(int(b), e);
            const plus = v - p;
            if (plus >= 2 && plus <= 60) {
                out.push(bin("+", base, int(plus)), bin("+", int(plus), base));
            }
            const minus = p - v;
            if (minus >= 2 && minus <= 60) {
                out.push(bin("−", base, int(minus)));
            }
            const from = v + p;
            if (from >= 2 && from <= 60) {
                out.push(bin("−", int(from), base));
            }
            if (v > 0 && v % p === 0 && v / p >= 2 && v / p <= 12) {
                out.push(bin("×", base, int(v / p)), bin("×", int(v / p), base));
            }
            if (v > 0 && p % v === 0 && p / v >= 2 && p / v <= 12) {
                out.push(bin("÷", base, int(p / v)));
            }
        }
        if (negativeCubes) {
            for (let b = 2; b <= 5; b++) {
                const cube = power(int(-b), 3);
                const plus = v + b ** 3;
                if (plus >= 2 && plus <= 60) {
                    out.push(bin("+", cube, int(plus)));
                }
                const minus = -v - b ** 3;
                if (minus >= 2 && minus <= 60) {
                    out.push(bin("−", cube, int(minus)));
                }
            }
        }
        return out;
    };

/** Family f: order of operations: two operators, with a pair of brackets where one is needed. */
function orderOfOperations(v: number): Node[] {
    const out: Node[] = [];
    for (let b = 2; b <= 12; b++) {
        for (let c = 2; c <= 12; c++) {
            const bc = int(b);
            const cc = int(c);
            const a = v - b * c;
            if (a >= 2 && a <= 60) {
                out.push(bin("+", int(a), bin("×", bc, cc)), bin("+", bin("×", bc, cc), int(a)));
            }
            const m = v + b * c;
            if (m >= 2 && m <= 100) {
                out.push(bin("−", int(m), bin("×", bc, cc)));
            }
            const k = b * c - v;
            if (k >= 2 && k <= 60) {
                out.push(bin("−", bin("×", bc, cc), int(k)));
            }
        }
    }
    for (let q = 2; q <= 12; q++) {
        for (let c = 2; c <= 12; c++) {
            // "a + b ÷ c" with b a multiple of c.
            const a = v - q;
            if (a >= 2 && a <= 60 && q * c <= 120) {
                out.push(bin("+", int(a), bin("÷", int(q * c), int(c))));
            }
        }
    }
    for (let c = 2; c <= 12; c++) {
        if (v % c !== 0) {
            continue;
        }
        const inner = v / c;
        for (let b = 2; b <= 30; b++) {
            const a = inner + b;
            if (a >= 2 && a <= 40) {
                out.push(bin("×", bin("−", int(a), int(b)), int(c)));
            }
            const x = inner - b;
            if (x >= 2 && inner >= 4) {
                out.push(bin("×", bin("+", int(x), int(b)), int(c)));
            }
        }
    }
    for (let c = 2; c <= 12; c++) {
        const total = v * c;
        for (let a = 2; a <= Math.min(60, total / 2); a++) {
            const b = total - a;
            if (b >= 2 && b <= 60 && v > 0) {
                out.push(bin("÷", bin("+", int(a), int(b)), int(c)));
            }
        }
    }
    return out;
}

const PERCENTS = [10, 20, 25, 50, 75, 150];

/** Family g: a percent of a whole number: "25% of 48". */
function percentsOf(v: number): Node[] {
    if (v <= 0) {
        return [];
    }
    const out: Node[] = [];
    for (const percent of PERCENTS) {
        const whole = (v * 100) / percent;
        if (Number.isInteger(whole) && whole >= 2 && whole <= 120) {
            out.push(of(pct(percent), int(whole)));
        }
    }
    return out;
}

/** Family h, grade 8: a square or cube root with one operation: "√49 + 3", "∛64 × 2", "20 − √16". */
function roots(v: number): Node[] {
    const out: Node[] = [];
    const radicals: [2 | 3, number][] = [
        ...range(2, 12).map((r): [2 | 3, number] => [2, r]),
        ...range(2, 6).map((r): [2 | 3, number] => [3, r]),
    ];
    for (const [index, r] of radicals) {
        const node = root(index, int(r ** index));
        const plus = v - r;
        if (plus >= 2 && plus <= 60) {
            out.push(bin("+", node, int(plus)), bin("+", int(plus), node));
        }
        const minus = r - v;
        if (minus >= 2 && minus <= 60) {
            out.push(bin("−", node, int(minus)));
        }
        const from = v + r;
        if (from >= 2 && from <= 60) {
            out.push(bin("−", int(from), node));
        }
        if (v > 0 && v % r === 0 && v / r >= 2 && v / r <= 12) {
            out.push(bin("×", node, int(v / r)), bin("×", int(v / r), node));
        }
        if (v > 0 && r % v === 0 && r / v >= 2 && r / v <= 12) {
            out.push(bin("÷", node, int(r / v)));
        }
    }
    return out;
}

/** Grade 5's families. */
export const G5_FAMILIES: Record<"s" | "m" | "d" | "c" | "p", Builder> = {
    s: bigSumsAndDifferences,
    m: twoDigitTimesDigit,
    d: decimalProducts,
    c: fractionsOf,
    p: parentheses,
};

/** Grade 6's families: no negative number anywhere. */
export const G6_FAMILIES: Record<"c" | "d" | "e" | "f" | "g", Builder> = {
    c: fractionsOf,
    d: decimalProducts,
    e: powersOf(SQUARES_AND_CUBES, false),
    f: orderOfOperations,
    g: percentsOf,
};

/** Grade 7's families: grade 6's, with integers and negative cubes. */
export const G7_FAMILIES: Record<"a" | "b" | "c" | "d" | "e" | "f" | "g", Builder> = {
    a: integerSums,
    b: integerProducts,
    c: fractionsOf,
    d: decimalProducts,
    e: powersOf(SQUARES_AND_CUBES, true),
    f: orderOfOperations,
    g: percentsOf,
};

/** Grade 8's families: grade 7's, with higher powers (e) and roots (h). */
export const G8_FAMILIES: Record<"a" | "b" | "c" | "d" | "e" | "f" | "g" | "h", Builder> = {
    ...G7_FAMILIES,
    e: powersOf([...SQUARES_AND_CUBES, ...HIGHER_POWERS], true),
    h: roots,
};
